import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { toast } from "sonner";
import {
    Plus,
    Search,
    Mail,
    Phone,
    Briefcase,
    MoreHorizontal,
} from "lucide-react";
import { getInitials, formatCurrency } from "../../lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const initialFormData = {
    name: "",
    email: "",
    phone: "",
    position: "",
    department: "",
    hourly_rate: "",
    availability: "full_time",
};

export const TeamPage = () => {
    const { get, post, del } = useApi();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);

    const fetchEmployees = useCallback(async () => {
        try {
            const data = await get("/employees");
            setEmployees(data);
        } catch (error) {
            toast.error("Erreur lors du chargement de l'équipe");
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await post("/employees", {
                ...formData,
                hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
            });
            toast.success("Employé ajouté avec succès");
            setDialogOpen(false);
            setFormData(initialFormData);
            fetchEmployees();
        } catch (error) {
            toast.error("Erreur lors de l'ajout de l'employé");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (employeeId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet employé ?")) return;

        try {
            await del(`/employees/${employeeId}`);
            toast.success("Employé supprimé");
            fetchEmployees();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const filteredEmployees = employees.filter(
        (emp) =>
            emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            emp.position?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getAvailabilityBadge = (availability) => {
        const styles = {
            full_time: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
            part_time: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
            contractor: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        };
        const labels = {
            full_time: "Temps plein",
            part_time: "Temps partiel",
            contractor: "Contractuel",
        };
        return <Badge className={styles[availability]}>{labels[availability]}</Badge>;
    };

    const getWorkloadColor = (percent) => {
        if (percent >= 80) return "text-red-600";
        if (percent >= 50) return "text-yellow-600";
        return "text-green-600";
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="team-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Équipe</h1>
                    <p className="text-muted-foreground mt-1">{employees.length} membres</p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-employee-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvel employé
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Nouvel employé</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <Label htmlFor="name">Nom *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        data-testid="employee-name-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email *</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        data-testid="employee-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="phone">Téléphone</Label>
                                    <Input
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="position">Poste</Label>
                                    <Input
                                        id="position"
                                        value={formData.position}
                                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="department">Département</Label>
                                    <Input
                                        id="department"
                                        value={formData.department}
                                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="hourly_rate">Taux horaire (€)</Label>
                                    <Input
                                        id="hourly_rate"
                                        type="number"
                                        value={formData.hourly_rate}
                                        onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>Disponibilité</Label>
                                    <Select
                                        value={formData.availability}
                                        onValueChange={(value) => setFormData({ ...formData, availability: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="full_time">Temps plein</SelectItem>
                                            <SelectItem value="part_time">Temps partiel</SelectItem>
                                            <SelectItem value="contractor">Contractuel</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="submit-employee-btn">
                                    {submitting ? "Ajout..." : "Ajouter"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Rechercher un employé..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-employees-input"
                />
            </div>

            {/* Team Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEmployees.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-muted-foreground">
                        <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Aucun employé trouvé</p>
                    </div>
                ) : (
                    filteredEmployees.map((employee) => (
                        <Card
                            key={employee.employee_id}
                            className="hover:shadow-md transition-all duration-200"
                            data-testid={`employee-card-${employee.employee_id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-12 w-12">
                                            <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                                                {getInitials(employee.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <h4 className="font-medium">{employee.name}</h4>
                                            {employee.position && (
                                                <p className="text-sm text-muted-foreground">{employee.position}</p>
                                            )}
                                        </div>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem
                                                className="text-destructive"
                                                onClick={() => handleDelete(employee.employee_id)}
                                            >
                                                Supprimer
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                <div className="space-y-2 mb-4">
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Mail className="w-4 h-4" />
                                        <span className="truncate">{employee.email}</span>
                                    </div>
                                    {employee.phone && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Phone className="w-4 h-4" />
                                            <span>{employee.phone}</span>
                                        </div>
                                    )}
                                    {employee.department && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Briefcase className="w-4 h-4" />
                                            <span>{employee.department}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between mb-3">
                                    {getAvailabilityBadge(employee.availability)}
                                    {employee.hourly_rate && (
                                        <span className="text-sm font-mono">{formatCurrency(employee.hourly_rate)}/h</span>
                                    )}
                                </div>

                                <div>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-muted-foreground">Charge de travail</span>
                                        <span className={`font-mono font-medium ${getWorkloadColor(employee.workload_percent)}`}>
                                            {employee.workload_percent}%
                                        </span>
                                    </div>
                                    <Progress value={employee.workload_percent} className="h-2" />
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
};
